import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Filter } from 'lucide-react';
import {
  FREE_RECORDING_LIMIT,
  FREE_TRACK_LIMIT,
  MAX_TRACKS,
} from '../features/recorder/audio/constants';
import { base64ToArrayBuffer } from '../features/recorder/audio/audioCodec';
import useRecordingsApi from '../features/recorder/hooks/useRecordingsApi';
import useAdvancedPlayer from '../features/recorder/hooks/useAdvancedPlayer';
import useMicCapture from '../features/recorder/hooks/useMicCapture';
import useLooperSessions from '../features/recorder/hooks/useLooperSessions';
import useLooperEngine from '../features/recorder/hooks/useLooperEngine';
import RecorderHeader from '../features/recorder/sections/RecorderHeader';
import MicStatusCard from '../features/recorder/sections/MicStatusCard';
import SingleTrackRecorderCard from '../features/recorder/sections/SingleTrackRecorderCard';
import AdvancedPlayerCard from '../features/recorder/sections/AdvancedPlayerCard';
import RecordingsFilters from '../features/recorder/sections/RecordingsFilters';
import MultitrackBar from '../features/recorder/sections/MultitrackBar';
import RecordingsList from '../features/recorder/sections/RecordingsList';
import EditMetadataModal from '../features/recorder/sections/EditMetadataModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecorderPage = () => {
  const { t } = useTranslation();
  const { isPremium } = useAuth();

  // Microphone capture (single-track recorder)
  const mic = useMicCapture();
  const {
    micPermission, micError,
    isRecording, audioUrl, audioBlob, recordingTime,
    streamRef,
    requestAccess: requestMicrophoneAccess,
    startRecording, stopRecording,
    discardCurrent: discardRecording,
  } = mic;

  const [savingTitle, setSavingTitle] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Recordings library + edit modal + filters
  const {
    recordings, fetchRecordings, isAtFreeLimit,
    searchQuery, setSearchQuery,
    filterTag, setFilterTag,
    filterBpmMin, setFilterBpmMin,
    filterBpmMax, setFilterBpmMax,
    showFilters, setShowFilters,
    allTags, filteredRecordings, resetFilters,
    editingRecording, editForm, setEditForm,
    newTag, setNewTag, savingEdit,
    openEditModal, closeEditModal, saveMetadata, addTag, removeTag,
    saveCurrent, removeRecording,
    downloadingId, downloadCurrent, downloadSaved,
  } = useRecordingsApi();

  // Saved-recording player with A/B loop + speed
  const {
    audioRef: playbackAudioRef,
    playingId, loadingPlayId, showAdvancedPlayer,
    currentTime, duration, playbackSpeed,
    loopEnabled, loopA, loopB, setLoopEnabled,
    playRecording: playSavedRecording,
    stopPlayback,
    handleTimeUpdate, handleLoadedMetadata,
    seek,
    handleSpeedChange,
    setPointA, setPointB,
    resetLoopPoints,
  } = useAdvancedPlayer();

  // Multitrack engine. Snapshot drives re-renders via useSyncExternalStore.
  const fetchAudioData = useCallback(async (id) => {
    const response = await axios.get(`${API}/recordings/${id}`);
    return response.data.audio_data;
  }, []);
  const { engine: looperEngine, snapshot: looperSnapshot } = useLooperEngine({ fetchAudioData });

  // selectedTrackIds is exposed as an array in the snapshot; the JSX
  // needs Set semantics (has, size, iteration).
  const selectedTrackIds = useMemo(
    () => new Set(looperSnapshot.selectedTrackIds),
    [looperSnapshot.selectedTrackIds]
  );
  const {
    trackVolumes, trackMuted, trackSoloed, masterVolume,
    isPlaying: isMultitrackPlaying,
    isLoading: multitrackLoading,
    elapsed: recMtElapsed,
    duration: recMtDuration,
    loop: multitrackLoop,
    overdubState, overdubMultiplier, overdubHistory,
  } = looperSnapshot;

  // Sessions (IndexedDB). Save/load talk to the engine for buffers + state.
  const {
    sessions,
    sessionName, setSessionName,
    sessionLoading, setSessionLoading,
    showSessions, setShowSessions,
    checkFreeLimit: checkSessionFreeLimit,
    saveSession: persistLooperSession,
    getById: getSessionById,
    removeById: deleteSessionById,
  } = useLooperSessions();

  const audioRef = useRef(null);

  // --- Multitrack thin wrappers on LooperEngine ---

  const toggleTrackSelection = async (id) => {
    if (selectedTrackIds.has(id)) {
      looperEngine.unselectTrack(id);
      return;
    }
    const limit = isPremium ? MAX_TRACKS : FREE_TRACK_LIMIT;
    if (selectedTrackIds.size >= limit) {
      toast.error(isPremium
        ? `Massimo ${MAX_TRACKS} tracce contemporanee`
        : `Massimo ${FREE_TRACK_LIMIT} tracce. Passa a PRO per ${MAX_TRACKS}.`);
      return;
    }
    try {
      await looperEngine.selectTrack(id);
    } catch (err) {
      console.error('selectTrack error', err);
      toast.error(t('recorder.multitrackError', { message: err.message || t('recorder.unknownError') }));
    }
  };

  const clearTrackSelection = () => {
    looperEngine.stop(true);
    for (const id of selectedTrackIds) looperEngine.unselectTrack(id);
  };

  const panicStop = () => {
    looperEngine.panic();
    toast.info(t('recorder.audioStopped'));
  };

  const stopMultitrackPlayback = (updateState = true) => {
    looperEngine.stop(updateState);
  };

  const playMultitrack = async () => {
    try {
      await looperEngine.play();
    } catch (err) {
      console.error('playMultitrack error', err);
      toast.error(err?.name === 'EncodingError' || err?.message?.includes?.('decod')
        ? t('recorder.multitrackFormatError')
        : t('recorder.multitrackError', { message: err?.message || t('recorder.unknownError') }));
    }
  };

  const updateTrackVolume = (id, value) => looperEngine.setVolume(id, value);
  const toggleMute = (id) => looperEngine.toggleMute(id);
  const toggleSolo = (id) => looperEngine.toggleSolo(id);
  const updateMasterVolume = (value) => looperEngine.setMaster(value);
  const setMultitrackLoop = (next) => looperEngine.setLoop(
    typeof next === 'function' ? next(multitrackLoop) : next
  );

  // Multiplier is applied at armOverdub time. Stage it in a ref so the UI
  // stays controlled until the next arm without needing engine state.
  const overdubMultiplierStagingRef = useRef(overdubMultiplier);
  const setOverdubMultiplier = (value) => {
    overdubMultiplierStagingRef.current = value;
  };

  // --- Overdub ---

  const canStartOverdub = () => {
    if (!isPremium) return false;
    if (!multitrackLoop) return false;
    if (!isMultitrackPlaying) return false;
    if (recMtDuration <= 0) return false;
    if (selectedTrackIds.size >= MAX_TRACKS) return false;
    return true;
  };

  const _processOverdubBlob = async (blob) => {
    try {
      const mimeType = blob.type || 'audio/webm';
      const actualDuration = Math.round(recMtDuration * (overdubMultiplierStagingRef.current || 1));
      const base64Audio = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const overdubTitle = `Overdub ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
      const response = await axios.post(`${API}/recordings`, {
        title: overdubTitle,
        audio_data: base64Audio,
        mime_type: mimeType,
        duration_seconds: actualDuration,
      });
      const newTrackId = response.data?.id;
      if (newTrackId) looperEngine.pushOverdubHistory(newTrackId);
      toast.success(t('recorder.overdubSaved'));
      await fetchRecordings();
    } catch (error) {
      console.error('processOverdubBlob error', error);
      toast.error(t('recorder.overdubSaveError'));
    }
  };

  const armOverdub = async () => {
    if (!canStartOverdub()) {
      if (!multitrackLoop || !isMultitrackPlaying) {
        toast.error(t('recorder.loopRequiredForOverdub'));
      } else if (selectedTrackIds.size >= MAX_TRACKS) {
        toast.error(`Limite ${MAX_TRACKS} tracce raggiunto`);
      }
      return;
    }
    if (micPermission !== 'granted') {
      await requestMicrophoneAccess();
      if (micPermission !== 'granted') {
        toast.error(t('recorder.micRequired'));
        return;
      }
    }
    if (!streamRef.current || !streamRef.current.active) {
      await requestMicrophoneAccess();
      return;
    }
    try {
      looperEngine.armOverdub({
        stream: streamRef.current,
        multiplier: overdubMultiplierStagingRef.current || overdubMultiplier,
        onFinished: _processOverdubBlob,
      });
      toast.info(`Overdub armato (${overdubMultiplierStagingRef.current || overdubMultiplier}x) - inizia al prossimo giro`);
    } catch (err) {
      console.error('armOverdub error', err);
      toast.error(err.message || t('recorder.overdubArmError'));
    }
  };

  const cancelOverdub = () => {
    looperEngine.cancelOverdub();
    toast.info(t('recorder.overdubCancelled'));
  };

  const undoLastOverdub = async () => {
    const trackId = looperEngine.popOverdubHistory();
    if (!trackId) return;
    looperEngine.unselectTrack(trackId);
    try {
      await axios.delete(`${API}/recordings/${trackId}`);
      toast.success(t('recorder.overdubRemoved'));
      await fetchRecordings();
    } catch (error) {
      console.error('undoLastOverdub error', error);
      toast.error(t('recorder.overdubRemoveError'));
    }
  };

  // --- Sessions (IndexedDB) ---

  const saveCurrentSession = async () => {
    if (!sessionName.trim()) {
      toast.error(t('recorder.sessionNameRequired'));
      return;
    }
    if (selectedTrackIds.size === 0) {
      toast.error(t('recorder.noTrackToSave'));
      return;
    }

    const allowed = await checkSessionFreeLimit();
    if (!allowed) return;

    setSessionLoading(true);
    try {
      const tracks = [];
      for (const trackId of selectedTrackIds) {
        const response = await axios.get(`${API}/recordings/${trackId}`);
        const audioData = response.data.audio_data;
        if (!audioData) continue;

        const arrayBuffer = base64ToArrayBuffer(audioData.split(',')[1]);
        tracks.push({
          trackId,
          title: response.data.title || `Track ${trackId}`,
          audioData: arrayBuffer,
          mimeType: response.data.mime_type || 'audio/webm',
          volume: trackVolumes[trackId] ?? 80,
          muted: trackMuted[trackId] || false,
          solo: trackSoloed[trackId] || false,
        });
      }

      if (tracks.length === 0) {
        toast.error(t('recorder.noValidAudioToSave'));
        return;
      }

      await persistLooperSession({
        id: `session_${Date.now()}`,
        name: sessionName.trim(),
        createdAt: new Date().toISOString(),
        tracks,
        masterVolume,
        loopEnabled: multitrackLoop,
      });
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error(t('recorder.sessionSaveError'));
    } finally {
      setSessionLoading(false);
    }
  };

  const loadSessionById = async (sessionId) => {
    setSessionLoading(true);
    try {
      const session = await getSessionById(sessionId);
      if (!session) {
        toast.error(t('recorder.sessionNotFound'));
        return;
      }

      looperEngine.stop(true);
      for (const id of selectedTrackIds) looperEngine.unselectTrack(id);

      // Decode each session track and push it into the engine's buffer
      // cache under a local_ sentinel id, then select it.
      const ctx = await looperEngine._ensureCtxResumed();
      let loadedCount = 0;
      for (const track of session.tracks) {
        try {
          const arrayBuffer = track.audioData.slice ? track.audioData : new Uint8Array(track.audioData).buffer;
          const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
          const localId = `local_${track.trackId}_${Date.now()}`;
          looperEngine.cacheLocalBuffer(localId, audioBuffer);
          await looperEngine.selectTrack(localId);
          if (typeof track.volume === 'number') looperEngine.setVolume(localId, track.volume);
          if (track.muted) looperEngine.toggleMute(localId);
          if (track.solo) looperEngine.toggleSolo(localId);
          loadedCount += 1;
        } catch (decodeErr) {
          console.warn('Failed to decode track:', track.trackId, decodeErr);
        }
      }

      if (loadedCount === 0) {
        toast.error(t('recorder.sessionInvalidTracks'));
        return;
      }

      looperEngine.setMaster(session.masterVolume ?? 80);
      looperEngine.setLoop(session.loopEnabled ?? false);

      toast.success(`Sessione "${session.name}" caricata!`);
      setShowSessions(false);
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error(t('recorder.sessionLoadError'));
    } finally {
      setSessionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-4 md:px-8 lg:px-12" data-testid="recorder-page">
      <div className="max-w-5xl mx-auto">
        <RecorderHeader />

        <MicStatusCard
          micPermission={micPermission}
          micError={micError}
          isRecording={isRecording}
          audioUrl={audioUrl}
          isAtFreeLimit={isAtFreeLimit}
          onRequestAccess={requestMicrophoneAccess}
        />

        <SingleTrackRecorderCard
          isRecording={isRecording}
          audioUrl={audioUrl}
          audioBlob={audioBlob}
          recordingTime={recordingTime}
          audioPreviewRef={audioRef}
          micPermission={micPermission}
          onStart={startRecording}
          onStop={stopRecording}
          onDiscard={discardRecording}
          showSaveDialog={showSaveDialog}
          setShowSaveDialog={setShowSaveDialog}
          savingTitle={savingTitle}
          setSavingTitle={setSavingTitle}
          onSave={async () => {
            const ok = await saveCurrent({ audioBlob, title: savingTitle });
            if (ok) {
              setShowSaveDialog(false);
              setSavingTitle('');
              discardRecording();
            }
          }}
          onDownloadCurrent={downloadCurrent}
          isAtFreeLimit={isAtFreeLimit}
          isPremium={isPremium}
        />

        {/* Hidden audio element driven by useAdvancedPlayer */}
        <audio
          ref={playbackAudioRef}
          className="hidden"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
        />

        {showAdvancedPlayer && (
          <AdvancedPlayerCard
            isPremium={isPremium}
            currentTime={currentTime}
            duration={duration}
            playbackSpeed={playbackSpeed}
            loopEnabled={loopEnabled}
            setLoopEnabled={setLoopEnabled}
            loopA={loopA}
            loopB={loopB}
            onSeek={seek}
            onSpeedChange={handleSpeedChange}
            onSetA={setPointA}
            onSetB={setPointB}
            onResetLoop={resetLoopPoints}
            onStopPlayback={stopPlayback}
          />
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold text-white">
              {t('recorder.recordings')}
            </h2>
            <div className="flex items-center gap-2">
              {!isPremium && (
                <span className="text-sm text-zinc-500">
                  {recordings.length}/{FREE_RECORDING_LIMIT} registrazioni
                </span>
              )}
              {isPremium && recordings.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-purple-400 hover:bg-purple-500/10"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  Filtri
                </Button>
              )}
            </div>
          </div>

          <RecordingsFilters
            isPremium={isPremium}
            showFilters={showFilters}
            recordings={recordings}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterTag={filterTag}
            setFilterTag={setFilterTag}
            filterBpmMin={filterBpmMin}
            setFilterBpmMin={setFilterBpmMin}
            filterBpmMax={filterBpmMax}
            setFilterBpmMax={setFilterBpmMax}
            allTags={allTags}
            filteredRecordings={filteredRecordings}
            onResetFilters={resetFilters}
          />

          {recordings.length > 0 && (
            <MultitrackBar
              isPremium={isPremium}
              recordings={recordings}
              selectedTrackIds={selectedTrackIds}
              isMultitrackPlaying={isMultitrackPlaying}
              multitrackLoading={multitrackLoading}
              multitrackLoop={multitrackLoop}
              recMtElapsed={recMtElapsed}
              recMtDuration={recMtDuration}
              masterVolume={masterVolume}
              trackVolumes={trackVolumes}
              trackMuted={trackMuted}
              trackSoloed={trackSoloed}
              overdubState={overdubState}
              overdubMultiplier={overdubMultiplier}
              overdubHistory={overdubHistory}
              onPlayMultitrack={playMultitrack}
              onStopMultitrack={stopMultitrackPlayback}
              onPanicStop={panicStop}
              onClearSelection={clearTrackSelection}
              onSetMultitrackLoop={setMultitrackLoop}
              onArmOverdub={armOverdub}
              onCancelOverdub={cancelOverdub}
              onUndoLastOverdub={undoLastOverdub}
              onSetOverdubMultiplier={setOverdubMultiplier}
              onToggleMute={toggleMute}
              onToggleSolo={toggleSolo}
              onUpdateTrackVolume={updateTrackVolume}
              onUpdateMasterVolume={updateMasterVolume}
              sessions={sessions}
              sessionName={sessionName}
              setSessionName={setSessionName}
              sessionLoading={sessionLoading}
              showSessions={showSessions}
              setShowSessions={setShowSessions}
              onSaveSession={saveCurrentSession}
              onLoadSession={loadSessionById}
              onDeleteSession={deleteSessionById}
            />
          )}

          <RecordingsList
            recordings={recordings}
            filteredRecordings={filteredRecordings}
            selectedTrackIds={selectedTrackIds}
            playingId={playingId}
            loadingPlayId={loadingPlayId}
            downloadingId={downloadingId}
            isPremium={isPremium}
            onToggleSelection={toggleTrackSelection}
            onPlay={playSavedRecording}
            onEdit={openEditModal}
            onDownload={downloadSaved}
            onDelete={removeRecording}
          />
        </motion.div>

        <EditMetadataModal
          editingRecording={editingRecording}
          editForm={editForm}
          setEditForm={setEditForm}
          newTag={newTag}
          setNewTag={setNewTag}
          savingEdit={savingEdit}
          isPremium={isPremium}
          onClose={closeEditModal}
          onSave={saveMetadata}
          onAddTag={addTag}
          onRemoveTag={removeTag}
        />
      </div>
    </div>
  );
};

export default RecorderPage;
